import React, {
  Dispatch,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { DeviceKeyMappingInfo } from 'redux/reducers/initialState'

import TextInput from '../common/TextInput'

type KeyMappingFormProps = {
  entityData: DeviceKeyMappingInfo
  setEntityData: Dispatch<React.SetStateAction<DeviceKeyMappingInfo>>
  setFormReady: Dispatch<React.SetStateAction<boolean>>
  setDataChanged: Dispatch<React.SetStateAction<boolean>>
  disabled?: any
}

const KeyMappingForm = forwardRef(
  (
    {
      entityData,
      setEntityData,
      setFormReady,
      disabled,
      setDataChanged,
    }: KeyMappingFormProps,
    ref,
  ) => {
    const [errors, setErrors]: any = useState({})
    const [showErrors, setShowErrors]: any = useState({})
    const isFormCompleted =
      entityData.deviceId != '' &&
      (entityData.userEmailAddress != '' || entityData.adGroupName != '')
    const regExDeviceId = new RegExp('^[a-zA-Z0-9_-]+$')
    const regExEmail = new RegExp(
      '^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
    )

    useEffect(() => {
      setFormReady(false)
    }, [])

    const isFormValid = () => {
      if (entityData.deviceId.trim() == '')
        errors.deviceId = 'Device ID is required'
      else if (regExDeviceId.test(entityData.deviceId) == false)
        errors.deviceId = 'Alphanumericals, dashes, or underscores only'
      else errors.deviceId = ''

      if (
        entityData.userEmailAddress != '' &&
        regExEmail.test(entityData.userEmailAddress) == false
      ) {
        errors.userEmailAddress = 'Email Address format is invalid'
        errors.adGroupName = ''
      } else {
        errors.userEmailAddress = ''

        if (
          entityData.userEmailAddress == '' &&
          entityData.adGroupName.trim() == ''
        ) {
          errors.userEmailAddress =
            'Email Address and/or Azure AD Group is required'
          errors.adGroupName = 'Email Address and/or Azure AD Group is required'
        } else {
          errors.userEmailAddress = ''
          errors.adGroupName = ''
        }
      }

      setErrors({ ...errors })
      return Object.keys(errors).filter((i) => errors[i] != '').length == 0
    }

    const validateForm = () => {
      if (isFormValid() && isFormCompleted) setFormReady(true)
      else setFormReady(false)
    }

    useImperativeHandle(ref, () => ({
      validate() {
        isFormValid()
        setShowErrors(Object.assign({}, errors))
      },
    }))

    const handleChange = (event) => {
      const key = event.target && event.target.name
      const value = event.target && event.target.value
      setEntityData({ ...entityData, [key]: value })
      setDataChanged(true)
    }

    return (
      <>
        <TextInput
          name="deviceId"
          label="Device ID"
          value={entityData.deviceId}
          onKeyUp={validateForm}
          onChange={handleChange}
          error={showErrors.deviceId}
          disabled={disabled && disabled.deviceId}
        />
        <hr />
        <TextInput
          name="userEmailAddress"
          label="Email Address"
          value={entityData.userEmailAddress}
          onKeyUp={validateForm}
          onChange={handleChange}
          error={showErrors.userEmailAddress}
          disabled={disabled && disabled.userEmailAddress}
        />
        <p className="responsive-font small font-weight-bold font-italic">
          - and/or -
        </p>
        <TextInput
          name="adGroupName"
          label="Azure AD Group"
          value={entityData.adGroupName}
          onKeyUp={validateForm}
          onChange={handleChange}
          error={showErrors.adGroupName}
          disabled={disabled && disabled.adGroupName}
        />
      </>
    )
  },
)

export default KeyMappingForm
